import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  MOMENT_DATEPICKER_PROVIDERS,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import {
  Invoice,
  TransactionAllocation,
} from '@principle-theorem/principle-core';
import { PatientPermissions } from '@principle-theorem/principle-core/features';
import {
  type IAccountCreditExtendedData,
  type IInvoice,
  type IStaffer,
  type ITransaction,
} from '@principle-theorem/principle-core/interfaces';
import {
  INamedDocument,
  isSameRef,
  mergeDayAndTime,
  resolveDocRefs,
  snapshot,
  sortByCreatedAt,
  toMoment,
  type DocumentReference,
  type WithRef,
} from '@principle-theorem/shared';
import { first } from 'lodash';
import * as moment from 'moment-timezone';
import { type Moment } from 'moment-timezone';
import { combineLatest, type Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { type ITransactionAmountDialogData } from '../../transaction-components/transaction-amount-dialog/transaction-amount-dialog.component';
import { AmendTransactionDateStore } from '../../transaction-components/transaction-edit-dialog/amend-transaction-date.store';
import { determineNewCreatedAtDate } from '../../transaction-helpers';

export interface IAmendAccountCreditTransactionDialogData
  extends ITransactionAmountDialogData {
  transaction: WithRef<ITransaction<IAccountCreditExtendedData>>;
  invoice: WithRef<IInvoice>;
  dateReceived?: Moment;
  attributedTo?: DocumentReference<IStaffer>;
}

export interface IAmendAccountCreditTransactionDialogResult {
  dateReceived?: Moment;
  attributedTo?: DocumentReference<IStaffer>;
}

class AmendAccountCreditTransactionFormGroup extends TypedFormGroup<IAmendAccountCreditTransactionDialogResult> {
  constructor(data: IAmendAccountCreditTransactionDialogData) {
    super({
      dateReceived: new TypedFormControl<Moment>(data.dateReceived),
      attributedTo: new TypedFormControl<
        DocumentReference<IStaffer> | undefined
      >(data.attributedTo),
    });
  }
}

@Component({
  selector: 'pr-amend-account-credit-transaction-dialog',
  templateUrl: './amend-account-credit-transaction-dialog.component.html',
  styleUrls: ['./amend-account-credit-transaction-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [...MOMENT_DATEPICKER_PROVIDERS, AmendTransactionDateStore],
  standalone: false,
})
export class AmendAccountCreditTransactionDialogComponent {
  form: AmendAccountCreditTransactionFormGroup;
  accountAdminPermission = PatientPermissions.AccountInvoiceAdmin;
  maxAmount$: Observable<number | undefined>;
  staff: INamedDocument<IStaffer>[];
  showAttributedTo: boolean;
  minDate$: Observable<moment.Moment | undefined>;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: IAmendAccountCreditTransactionDialogData,
    private _dialogRef: MatDialogRef<
      AmendAccountCreditTransactionDialogComponent,
      IAmendAccountCreditTransactionDialogResult
    >,
    public amendDateStore: AmendTransactionDateStore
  ) {
    this.form = new AmendAccountCreditTransactionFormGroup(data);
    this.staff = Invoice.staffOnInvoice(this.data.invoice);
    this.showAttributedTo =
      this.staff.length > 1 ||
      TransactionAllocation.hasUnallocatedAmount(this.data.invoice);
    this.amendDateStore.loadAmendDates(data.transaction);

    this.minDate$ = combineLatest([
      this._lastCreditTakenDate(data.transaction),
      this.amendDateStore.minDate$,
    ]).pipe(
      map(([lastCreditTakenDate, minDate]) =>
        minDate && minDate.isAfter(lastCreditTakenDate)
          ? minDate
          : lastCreditTakenDate
      )
    );
  }

  async submit(): Promise<void> {
    if (this.form.invalid) {
      return;
    }

    const data = this.form.value;
    const dateReceived = data.dateReceived
      ? await determineNewCreatedAtDate(
          this.data.transaction,
          mergeDayAndTime(data.dateReceived, moment()),
          await snapshot(this.minDate$),
          await snapshot(this.amendDateStore.maxDate$)
        )
      : undefined;

    this._dialogRef.close({
      ...this.form.value,
      dateReceived,
    });
  }

  comparePractitionerRefFn(
    value: DocumentReference<IStaffer>,
    practitioner: DocumentReference<IStaffer>
  ): boolean {
    if (!value || !practitioner) {
      return false;
    }
    return isSameRef(value, practitioner);
  }

  private async _lastCreditTakenDate(
    transaction: WithRef<ITransaction<IAccountCreditExtendedData>>
  ): Promise<Moment | undefined> {
    const creditRefs =
      transaction.extendedData?.accountCreditsUsed.map(
        (creditUsed) => creditUsed.ref
      ) ?? [];
    if (creditRefs.length === 0) {
      return;
    }
    const credits = await resolveDocRefs(creditRefs);

    const mostRecentCredit = first(credits.sort(sortByCreatedAt));
    if (!mostRecentCredit) {
      return;
    }
    return toMoment(mostRecentCredit.createdAt);
  }
}
